/**
 * The `demo_first_run` payload contract — types + the defensive parser.
 *
 * The demo control plane writes `demo_first_run` into the demo config blob,
 * the SAME workspace-settings channel that already carries `tour` and
 * `welcome_message`; CD passes it through GET /me as `me.demo.first_run`
 * (apps/api routes/me.py). This module is the ONLY place that trusts it, and
 * it trusts nothing: the payload is authored per-demo by a tool outside this
 * repo, so every field is re-checked here.
 *
 * FROZEN SHAPE (a checked-in example lives in ./fixture.ts):
 *
 *   {
 *     "enabled": true,
 *     "context": {"pain_line", "product_line", "fiction_line", "expect_line",
 *                 "client_name"},
 *     "files": [{"name": "acme-loan-tape.csv", "ext": "csv"}],
 *     "file_count": 12,
 *     "cards": [{"workflow_id","title","tagline","href","live_build"?,
 *                "replay"?: [{"t", "line"}]}],
 *     "promise": {"line", "pages": ["..."]},
 *     "replay": [{"t": 0.4, "line": "ingested acme-loan-tape.csv → workspace"}],
 *     "reveal": {"href", "pages": [{"label","href"}]},
 *     "proof": {"enabled": true},
 *     "receipt_url": "https://.../r/<token>",
 *     "ask": {"prompt", "href"},
 *     "repeat": {"workflow_title", "owner_name", "output_title", "cadence"},
 *     "live_build_recording": {"steps": [{"name","label","ms"}], "total_ms",
 *                              "artifact_title", "href",
 *                              "outcome": {"stats": [{"label","value","delta",
 *                                                     "sentiment"}], "decision"}}
 *   }
 *
 * `context`, `files`, `live_build_recording`, `context.pain_line`, `repeat` and
 * the recording's `outcome` are the LATER keys — demos authored before them
 * carry none, and each is independently optional: an absent `context` drops the
 * "what this is" beat, absent `files` drops the "what we loaded" beat, an
 * absent recording means a second seat sees the old one-line cached notice
 * instead of a replayed build, no `pain_line` keeps the product-first opening,
 * no `repeat` drops the repeatability beat, and no `outcome` leaves the reveal
 * exactly as it was. Nothing else changes.
 *
 * `parseFirstRunPayload` returns `null` — meaning "render nothing, the app
 * behaves exactly as it did before this feature existed" — for an absent key,
 * `enabled` anything other than literal `true`, a non-object, or a payload
 * whose every substantive section is missing/unusable. Individual malformed
 * ENTRIES are dropped rather than failing the whole payload, so one bad replay
 * line can't cost a prospect the whole arc.
 *
 * All hrefs arrive RESOLVED — this module resolves nothing. It only refuses
 * hrefs it would be unsafe to hand to the router or an anchor: in-app targets
 * must be root-relative ("/w/acme/c/01J…"), the receipt must be http(s).
 */

/**
 * Who this demo is and what it is FOR — the sentences the arc opens on.
 *
 * A prospect meeting a demo cold knows neither the product nor the fiction the
 * data belongs to; both used to be inferred from a deliverable that appeared
 * out of nowhere. `fiction_line` is the one that discloses the client is
 * invented, and it is shown at full size rather than as a footnote: saying so
 * up front is the feature.
 *
 * `pain_line` is the LATER key that reorders the opening. An arc that opens by
 * explaining the product opens on the seller's subject; an arc that opens on
 * the prospect's own recurring problem opens on theirs, and `expect_line` is
 * then the release of that tension rather than a footnote under a welcome. So
 * when `pain_line` is authored the arc plays problem -> files and the product
 * explanation is DEMOTED out of the rail entirely (it lives on the sign-in
 * page); when it is not, the old product-first opening stands untouched.
 *
 * `client_name` is the display name the later beats speak in ("We loaded 12 of
 * Acme Capital's files"). It is carried HERE rather than read off the demo
 * config so the rail's copy and the platform's authored lines can never
 * disagree about what the fictional client is called; the chrome still passes
 * `me.demo.display_name` as the fallback.
 */
export type FirstRunContext = {
  /** The problem this prospect already has, in their words. Empty = unauthored,
   *  and the arc keeps its product-first opening. */
  pain_line: string;
  product_line: string;
  fiction_line: string;
  client_name: string;
  /** What the prospect is about to GET, spoken before anything is shown. */
  expect_line: string;
};

/** One file in the "what we loaded" strip. `ext` is a short tag ("csv",
 *  "xlsx") rendered beside the name — never a MIME type, never a path. */
export type FirstRunFile = {
  name: string;
  ext: string;
  /** One plain line saying what the file IS — a name like
   *  q3-2026-nav-bridge.csv teaches a prospect nothing on its own. */
  description: string;
};

/** One workflow card in the `choose` state.
 *
 *  `live_build` is a MARK, not a payload: when the control plane sets it, the
 *  replay state for this card is a real build the server performs on entry
 *  (POST /demo/live-build) rather than the provisioning run's recorded log.
 *  The facts it is built from deliberately never reach the browser — they
 *  live in a config key `/me` does not pass through. */
export type FirstRunCard = {
  workflow_id: string;
  title: string;
  tagline: string;
  href: string;
  live_build: boolean;
  /** The replay of THIS card's own run — the data room, its own
   *  deliverable's build, its own workflow's run — sliced server-side from
   *  the whole feed. `null` from a platform that predates it: the card then
   *  plays the top-level `replay`, as it always did. (The whole feed merged
   *  every baked run: Oxera's "The run" ended on two "Analysis complete"
   *  lines, 1.8 and 3.3 minutes, under whichever card was picked.) */
  replay: FirstRunReplayLine[] | null;
};

/** The generated promise shown before the replay: a page list and a line. */
export type FirstRunPromise = {
  line: string;
  pages: string[];
};

/** One line of the REAL provisioning event log. `t` is the recorded offset in
 *  seconds; it fixes ORDER only — playback is paced by the rail (see
 *  ./machine.ts `replayIntervalMs`), because a real run's wall clock is
 *  minutes and the arc's whole budget is ninety seconds. */
export type FirstRunReplayLine = {
  t: number;
  line: string;
};

export type FirstRunRevealPage = {
  label: string;
  href: string;
};

/** The hero deliverable, deep-linked, plus the pages worth jumping to.
 *
 *  `outcome` is the LATER key: /me computes it server-side from the demo's
 *  live-build facts (the same function a fresh build's HTTP answer uses, so
 *  the two can never disagree) whenever the stored reveal lacks one. It is
 *  the FALLBACK outcome — a build's own answer still wins — and it is what
 *  keeps the outcome-first reveal alive in replay mode and after a reconcile
 *  drops the recording, where the arc used to degrade to the statless
 *  provenance screen. */
export type FirstRunReveal = {
  href: string;
  pages: FirstRunRevealPage[];
  outcome: FirstRunOutcome | null;
  /** The LATER key beside `outcome`, same injector: the in-app address of
   *  the demo's hero deliverable, found read-only by /me — the live-built
   *  deck when the demo has one, otherwise the newest artifact on the
   *  reveal canvas (authored-deliverable demos never had an address at
   *  all). It is what lets the open button reach the actual file on paths
   *  that never call the build route. Empty -> the reveal opens the working
   *  page, as it always did. */
  pack_href: string;
  /** The deliverable's file format ("pptx", "docx", "pdf", …), injected
   *  beside `pack_href` so the open button can call the thing what it is —
   *  "Open the document" for a memo, "Open the pack" only for a deck. */
  pack_format: string;
};

/** The composer moment: a prompt worth asking and where to ask it. */
export type FirstRunAsk = {
  prompt: string;
  href: string;
};

/** One step of a recorded live build. Identical in shape to the live
 *  `LiveBuildStep` the server answers with (api/demo.ts) — deliberately, since
 *  the rail renders the two through the same code path. */
export type FirstRunRecordedStep = {
  name: string;
  label: string;
  ms: number;
};

/** How a delta reads. `neutral` is the fallback for anything unrecognised —
 *  a stat the server didn't classify must not be coloured as good news. */
export type FirstRunSentiment = "good" | "bad" | "neutral";

/** One headline number from the finished deliverable.
 *
 *  Every string is SERVER-FORMATTED. The rail does no arithmetic, no rounding
 *  and no unit-guessing on these — it lays them out. `delta` also carries its
 *  own sign ("+1.2% vs plan", "-120bps"), so the sentiment colour is a second
 *  channel rather than the only one. */
export type FirstRunOutcomeStat = {
  label: string;
  value: string;
  /** Movement, already formatted with its sign. Empty = no delta to show. */
  delta: string;
  sentiment: FirstRunSentiment;
};

/**
 * What the deliverable actually SAYS — the business outcome, not the file.
 *
 * The reveal used to lead with provenance ("this file did not exist when you
 * clicked"), which is the most interesting sentence in the room to the people
 * who built it and the least interesting to the person being sold to. So the
 * reveal now leads with the numbers and the decision they imply, and the
 * provenance line demotes to a supporting caption underneath.
 *
 * Absent (old recordings, a build the server hasn't taught to summarise) and
 * the reveal renders exactly as it did before this key existed.
 */
export type FirstRunOutcome = {
  stats: FirstRunOutcomeStat[];
  /** The call the numbers imply, in one sentence. */
  decision: string;
};

/**
 * The repeatability reveal — "and then it keeps running".
 *
 * The arc used to close on a receipt, which ends on what this ONE run produced.
 * The thing worth buying is not the run; it is that the run becomes a standing
 * job with an owner, an output and a cadence. So the last scripted beat turns
 * the demo into a workflow the prospect can picture on their own calendar.
 *
 * The cadence row is DISPLAY ONLY — nothing here schedules anything, and the
 * beat says so in words rather than letting a rendered radio imply otherwise.
 */
export type FirstRunRepeat = {
  workflow_title: string;
  owner_name: string;
  output_title: string;
  /** How often it would run, in the platform's own words ("Every quarter, 5
   *  business days after close"). Empty = the schedule row is not shown. */
  cadence: string;
};

/**
 * The first fresh live build, kept so every LATER seat can watch it happen.
 *
 * The live build is cached after its first run: seat 2 used to get a single
 * flat line ("… was already built from these figures"), which is the one moment
 * of the arc worth watching collapsed into a sentence. The platform persists
 * the first real run's steps and timings here, and the rail replays THAT —
 * honestly labelled as a recording, never as a build happening now.
 *
 * Every number in it is the server's own measurement from the run that
 * actually happened. The rail invents none of them.
 */
export type FirstRunRecording = {
  steps: FirstRunRecordedStep[];
  total_ms: number;
  artifact_title: string;
  href: string;
  /** What that run CONCLUDED. Recorded beside the timings so a second seat
   *  gets the same outcome-first reveal the first seat got. */
  outcome: FirstRunOutcome | null;
};

export type FirstRunPayload = {
  context: FirstRunContext | null;
  files: FirstRunFile[];
  /** How many files the data room holds — the number "We loaded N" states.
   *  The platform caps `files` at a screenful, so the list's length is NOT
   *  the count (a 22-file room read "We loaded 16"); and the same number
   *  fills any `{file_count}` in the context lines, so the opening and this
   *  screen cannot disagree. Never less than `files.length`. */
  file_count: number;
  cards: FirstRunCard[];
  promise: FirstRunPromise | null;
  replay: FirstRunReplayLine[];
  reveal: FirstRunReveal | null;
  /** The trust beat's platform mark — "challenge a number", straight after the
   *  reveal. A MARK rather than content: the beat's copy is the rail's own
   *  (it invites clicking the evidence markers the product already renders),
   *  so the platform only says whether this demo's content is staged for it.
   *  Absent for demos authored before the beat existed — their arc is
   *  unchanged. */
  proof: boolean;
  receipt_url: string;
  ask: FirstRunAsk | null;
  repeat: FirstRunRepeat | null;
  live_build_recording: FirstRunRecording | null;
};

/** How many file chips the `inputs` strip will render. A data room can hold
 *  hundreds; the beat is "here is what we loaded", not an inventory, and the
 *  strip says how many more there are. */
export const FIRST_RUN_MAX_FILES = 14;

/** How many headline numbers the outcome card will show. The contract caps the
 *  server at four; the rail enforces it too, because a stat row that wraps to
 *  two lines stops being a glance. */
export const FIRST_RUN_MAX_STATS = 4;

// ── primitive guards ─────────────────────────────────────────────────────────

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** A trimmed string, or "" for anything that isn't a usable string. */
function str(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

/**
 * An in-app destination the rail may hand to `router.history.push`.
 *
 * Root-relative only. This rejects `javascript:` / `data:` / absolute URLs and
 * protocol-relative `//evil.example` — the payload is authored outside this
 * repo, and a navigation target is the one field where a bad value does more
 * than render wrong.
 */
function appHref(value: unknown): string {
  const raw = str(value);
  if (!raw.startsWith("/") || raw.startsWith("//")) return "";
  return raw;
}

/** An external link the rail may render as an anchor (the run receipt). */
function externalHref(value: unknown): string {
  const raw = str(value);
  return /^https?:\/\//i.test(raw) ? raw : "";
}

// ── section parsers ──────────────────────────────────────────────────────────

function parseContext(value: unknown): FirstRunContext | null {
  if (!isRecord(value)) return null;
  const pain_line = str(value.pain_line);
  const product_line = str(value.product_line);
  const fiction_line = str(value.fiction_line);
  const client_name = str(value.client_name);
  const expect_line = str(value.expect_line);
  // The `what` beat is the product line and the disclosure; the `problem` beat
  // is the pain line. A context that carries only a client name is neither — it
  // still parses (the name is spoken by the later states), but with nothing to
  // say it renders no state.
  if (!pain_line && !product_line && !fiction_line && !client_name && !expect_line) return null;
  return { pain_line, product_line, fiction_line, client_name, expect_line };
}

function parseFiles(value: unknown): FirstRunFile[] {
  if (!Array.isArray(value)) return [];
  const out: FirstRunFile[] = [];
  for (const item of value) {
    // A bare string is accepted as a filename — the ext is then derived, so a
    // control plane that ships `["a.csv", "b.xlsx"]` still gets the strip.
    if (typeof item === "string") {
      const name = item.trim();
      if (name) out.push({ name, ext: extOf(name), description: "" });
      continue;
    }
    if (!isRecord(item)) continue;
    const name = str(item.name);
    if (!name) continue; // a chip with no name is not a chip
    out.push({
      name,
      ext: str(item.ext).replace(/^\./, "").toLowerCase() || extOf(name),
      description: str(item.description),
    });
  }
  return out;
}

/** The data room's size: the platform's whole-room count, or the list's own
 *  length for a payload that predates it (or a count that undercounts the
 *  very list it came with). */
function parseFileCount(value: unknown, listed: number): number {
  const n = typeof value === "number" && Number.isInteger(value) ? value : 0;
  return Math.max(n, listed);
}

/** The extension tag derived from a filename, when the payload named none.
 *  Bounded: an "extension" longer than five characters is a full stop in a
 *  sentence, not a file type. */
function extOf(name: string): string {
  const dot = name.lastIndexOf(".");
  if (dot <= 0 || dot === name.length - 1) return "";
  const ext = name.slice(dot + 1).toLowerCase();
  return /^[a-z0-9]{1,5}$/.test(ext) ? ext : "";
}

/**
 * The outcome card's material — from the recording's blob OR from the live
 * build's HTTP answer.
 *
 * Exported because those two arrive through different doors and BOTH are
 * untrusted: the blob is authored outside this repo, and `POST /demo/live-build`
 * is typed in api/demo.ts but not validated there. One guard, one place.
 *
 * Nothing here is computed. A stat the prospect can't read (no label, or no
 * value) is dropped rather than rendered half-empty, an unrecognised sentiment
 * degrades to `neutral` rather than colouring an unknown number as good news,
 * and an outcome with neither a readable stat nor a decision is no outcome —
 * which puts the reveal back to exactly the screen it was before this key.
 */
export function parseFirstRunOutcome(value: unknown): FirstRunOutcome | null {
  if (!isRecord(value)) return null;
  const stats: FirstRunOutcomeStat[] = [];
  if (Array.isArray(value.stats)) {
    for (const item of value.stats) {
      if (stats.length >= FIRST_RUN_MAX_STATS) break;
      if (!isRecord(item)) continue;
      const label = str(item.label);
      const statValue = str(item.value);
      // Half a stat is not a stat: a number with no label says nothing, and a
      // label with no number is a promise the card can't keep.
      if (!label || !statValue) continue;
      const raw = str(item.sentiment).toLowerCase();
      const sentiment: FirstRunSentiment =
        raw === "good" || raw === "bad" ? raw : "neutral";
      stats.push({ label, value: statValue, delta: str(item.delta), sentiment });
    }
  }
  const decision = str(value.decision);
  if (stats.length === 0 && !decision) return null;
  return { stats, decision };
}

function parseRepeat(value: unknown): FirstRunRepeat | null {
  if (!isRecord(value)) return null;
  const workflow_title = str(value.workflow_title);
  // The beat IS the workflow — "this becomes a standing job called X". With no
  // name for it, an owner and a cadence are rows under a missing headline, so
  // the state is dropped whole rather than rendered incomplete.
  if (!workflow_title) return null;
  return {
    workflow_title,
    owner_name: str(value.owner_name),
    output_title: str(value.output_title),
    cadence: str(value.cadence),
  };
}

function parseRecording(value: unknown): FirstRunRecording | null {
  if (!isRecord(value)) return null;
  const steps: FirstRunRecordedStep[] = [];
  if (Array.isArray(value.steps)) {
    for (const item of value.steps) {
      if (!isRecord(item)) continue;
      const name = str(item.name);
      const label = str(item.label);
      // A step the prospect can't read is a step that can't be replayed.
      if (!name && !label) continue;
      const ms =
        typeof item.ms === "number" && Number.isFinite(item.ms) && item.ms >= 0 ? item.ms : 0;
      steps.push({ name, label, ms });
    }
  }
  // No steps, nothing to replay — and the caller then falls back to the plain
  // cached notice rather than showing an empty log under a "watch it" kicker.
  if (steps.length === 0) return null;
  const total_ms =
    typeof value.total_ms === "number" && Number.isFinite(value.total_ms) && value.total_ms >= 0
      ? value.total_ms
      : steps.reduce((sum, s) => sum + s.ms, 0);
  return {
    steps,
    total_ms,
    artifact_title: str(value.artifact_title),
    href: appHref(value.href),
    outcome: parseFirstRunOutcome(value.outcome),
  };
}

function parseCards(value: unknown): FirstRunCard[] {
  if (!Array.isArray(value)) return [];
  const out: FirstRunCard[] = [];
  for (const item of value) {
    if (!isRecord(item)) continue;
    const title = str(item.title);
    const href = appHref(item.href);
    // A card the prospect can't read or can't click isn't a card.
    if (!title || !href) continue;
    out.push({
      workflow_id: str(item.workflow_id),
      title,
      tagline: str(item.tagline),
      href,
      // Literal `true` only. Anything else — a truthy string, a 1, an
      // object — means the control plane did not mark this card, and a
      // guess here would promise a live build the server will 404.
      live_build: item.live_build === true,
      replay: Array.isArray(item.replay) ? parseReplay(item.replay) : null,
    });
  }
  return out;
}

function parsePromise(value: unknown): FirstRunPromise | null {
  if (!isRecord(value)) return null;
  const line = str(value.line);
  const pages = Array.isArray(value.pages) ? value.pages.map(str).filter((p) => p.length > 0) : [];
  if (!line && pages.length === 0) return null;
  return { line, pages };
}

function parseReplay(value: unknown): FirstRunReplayLine[] {
  if (!Array.isArray(value)) return [];
  const out: FirstRunReplayLine[] = [];
  for (const item of value) {
    if (!isRecord(item)) continue;
    const line = str(item.line);
    if (!line) continue;
    const t = typeof item.t === "number" && Number.isFinite(item.t) ? item.t : out.length;
    out.push({ t, line });
  }
  // `t` is the recorded offset; a control plane that emits them out of order
  // still gets a log that reads forwards. Stable for equal `t`.
  return out
    .map((entry, i) => ({ entry, i }))
    .sort((a, b) => a.entry.t - b.entry.t || a.i - b.i)
    .map(({ entry }) => entry);
}

function parseReveal(value: unknown): FirstRunReveal | null {
  if (!isRecord(value)) return null;
  const href = appHref(value.href);
  const pages: FirstRunRevealPage[] = [];
  if (Array.isArray(value.pages)) {
    for (const item of value.pages) {
      if (!isRecord(item)) continue;
      const label = str(item.label);
      const pageHref = appHref(item.href);
      if (!label || !pageHref) continue;
      pages.push({ label, href: pageHref });
    }
  }
  const outcome = parseFirstRunOutcome(value.outcome);
  const pack_href = appHref(value.pack_href);
  const pack_format = pack_href ? str(value.pack_format) : "";
  // Nowhere to go, nothing to offer AND nothing to say -> no reveal state.
  if (!href && pages.length === 0 && outcome === null) return null;
  return { href, pages, outcome, pack_href, pack_format };
}

function parseAsk(value: unknown): FirstRunAsk | null {
  if (!isRecord(value)) return null;
  const prompt = str(value.prompt);
  if (!prompt) return null;
  return { prompt, href: appHref(value.href) };
}

// ── the entry point ──────────────────────────────────────────────────────────

/**
 * Parse `me.demo.first_run` into something the rail can render, or `null`.
 *
 * `null` is the load-bearing case: absent key, `enabled: false`, a malformed
 * blob, or a payload with no substantive state left after validation. Older
 * demos — which carry no `demo_first_run` at all — always land here, so they
 * behave exactly as they did before the rail existed.
 */
export function parseFirstRunPayload(raw: unknown): FirstRunPayload | null {
  if (!isRecord(raw)) return null;
  if (raw.enabled !== true) return null;

  const files = parseFiles(raw.files);
  const payload: FirstRunPayload = {
    context: parseContext(raw.context),
    files,
    file_count: parseFileCount(raw.file_count, files.length),
    cards: parseCards(raw.cards),
    promise: parsePromise(raw.promise),
    replay: parseReplay(raw.replay),
    reveal: parseReveal(raw.reveal),
    // Any object counts as the mark — the platform emits `{"enabled": true}`
    // today, but the beat must not vanish if that shape ever grows fields.
    proof: isRecord(raw.proof),
    receipt_url: externalHref(raw.receipt_url),
    ask: parseAsk(raw.ask),
    repeat: parseRepeat(raw.repeat),
    live_build_recording: parseRecording(raw.live_build_recording),
  };

  // A rail that would open on the welcome line and immediately offer the exit
  // is worse than no rail: refuse unless at least one scripted state survived.
  // The recording is deliberately NOT in this list — it is the replay state's
  // material, not a state of its own, so a payload carrying only a recording
  // still has no arc to play. `repeat` IS a state of its own, so it counts.
  const substantive =
    payload.context !== null ||
    payload.files.length > 0 ||
    payload.cards.length > 0 ||
    payload.promise !== null ||
    payload.replay.length > 0 ||
    payload.reveal !== null ||
    payload.ask !== null ||
    payload.repeat !== null;
  return substantive ? payload : null;
}
